import { useState, useRef, useEffect, type KeyboardEvent, type ClipboardEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { authApi } from '@/features/auth/api/auth.api';
import type { AuthResponse } from '@/types';
import { Button } from '@/shared/components/ui';
import { STORAGE_KEYS } from '@/utils/constants';
import { legalConfig } from '@/shared/config/legal';
import { GOOGLE_ACCOUNT_PHONE_MESSAGE } from '../constants';

const OTP_LENGTH = 6;

interface OTPVerifyFormProps {
  phone: string;
  intent?: string;
  from?: string;
  travelerSearch?: boolean;
  onSuccess: (data: AuthResponse) => void;
  onBack: () => void;
}

export function OTPVerifyForm({ phone, intent, from, travelerSearch, onSuccess, onBack }: OTPVerifyFormProps) {
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [resendCountdown, setResendCountdown] = useState(30);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const verifyMutation = useMutation({
    mutationFn: (otp: string) =>
      authApi.verifyOtp(phone, otp, { intent, from, travelerSearch }),
    onSuccess: (data) => {
      localStorage.removeItem(STORAGE_KEYS.OTP_PHONE);
      onSuccess(data);
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      const msg = err?.response?.data?.message || 'Invalid OTP. Please try again.';
      setError(msg);
      setDigits(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    },
  });

  const resendMutation = useMutation({
    mutationFn: () =>
      authApi.resendOtp(phone, {
        smsConsent: true,
        smsConsentVersion: legalConfig.smsConsentVersion,
        smsConsentSource: 'web_otp_resend',
      }),
    onSuccess: () => {
      setError('');
      setResendCountdown(30);
      setDigits(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    },
    onError: () => setError('Failed to resend OTP. Please try again.'),
  });

  const handleDigitChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value.slice(-1);
    setDigits(newDigits);
    setError('');
    if (value && index < OTP_LENGTH - 1) inputRefs.current[index + 1]?.focus();
    const fullOtp = newDigits.join('');
    if (fullOtp.length === OTP_LENGTH) verifyMutation.mutate(fullOtp);
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const newDigits = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((char, i) => {
      newDigits[i] = char;
    });
    setDigits(newDigits);
    if (pasted.length === OTP_LENGTH) verifyMutation.mutate(pasted);
    else inputRefs.current[pasted.length]?.focus();
  };

  const maskedPhone = phone.replace(/(\+?\d{2,3})\d+(\d{2})$/, '$1****$2');

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        size="small"
        disableMotion
        intent="ghost"
        sx={{ mb: 2, ml: -1 }}
        onClick={onBack}
      >
        Back
      </Button>

      <Typography variant="h5" fontWeight={700} textAlign="center" sx={{ mb: 0.5 }}>
        Enter OTP
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3, lineHeight: 1.65 }}>
        We sent a 6-digit code to{' '}
        <Box component="span" fontWeight={600} color="text.primary">
          {maskedPhone}
        </Box>
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
          {(error === GOOGLE_ACCOUNT_PHONE_MESSAGE || error.includes('Google Sign-In')) && (
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              Go back and continue with Google Sign-In.
            </Typography>
          )}
        </Alert>
      )}

      <Box
        sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, justifyContent: 'center', mb: 3 }}
        onPaste={handlePaste}
      >
        {digits.map((digit, index) => (
          <Box
            key={index}
            component="input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={digit}
            onChange={(e) => handleDigitChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e as KeyboardEvent<HTMLInputElement>)}
            ref={(el) => {
              inputRefs.current[index] = el as HTMLInputElement | null;
            }}
            sx={(theme) => ({
              width: { xs: 44, sm: 52 },
              height: { xs: 56, sm: 64 },
              border: '2px solid',
              borderColor: digit ? 'secondary.main' : 'divider',
              borderRadius: 2,
              textAlign: 'center',
              fontSize: { xs: '1.4rem', sm: '1.6rem' },
              fontWeight: 700,
              outline: 'none',
              bgcolor: 'background.default',
              color: 'text.primary',
              transition: 'border-color 0.2s, box-shadow 0.2s',
              '&:focus': {
                borderColor: 'secondary.main',
                bgcolor: 'background.paper',
                boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.18)}`,
              },
            })}
          />
        ))}
      </Box>

      {verifyMutation.isPending && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={28} color="secondary" />
        </Box>
      )}

      <Button
        intent="primary-dark"
        fullWidth
        size="large"
        disableMotion
        onClick={() => verifyMutation.mutate(digits.join(''))}
        disabled={digits.join('').length !== OTP_LENGTH || verifyMutation.isPending}
        sx={{ py: 1.35, mb: 2 }}
      >
        Verify OTP
      </Button>

      <Box sx={{ textAlign: 'center' }}>
        {resendCountdown > 0 ? (
          <Typography variant="body2" color="text.secondary">
            Resend OTP in{' '}
            <Box component="span" fontWeight={600} color="secondary.main">
              {resendCountdown}s
            </Box>
          </Typography>
        ) : (
          <Button
            variant="text"
            size="small"
            disableMotion
            onClick={() => resendMutation.mutate()}
            disabled={resendMutation.isPending}
            color="secondary"
          >
            {resendMutation.isPending ? 'Sending...' : 'Resend OTP'}
          </Button>
        )}
      </Box>
    </Box>
  );
}
